import React from "react";
import {
  FileText,
  CheckCircle,
  Shield,
  Award,
  ArrowRight,
  Zap,
  Users,
  Clock,
  Lock,
  Book,
  DollarSign,
  Star,
} from "lucide-react";
import { Link } from "react-router-dom";
import PopupButton from "../components/PopupButton";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import TestimonialSlider from "../components/TestimonialSlider";
import HowItWorksSlider from "../components/HowItWorksSlider";
import WhyYouNeedItSlider from "../components/WhyYouNeedItSlider";
import WhyChooseUsSlider from "../components/WhyChooseUsSlider";
import BenefitsSlider from "../components/BenefitsSlider";
import { copyrightPricing, copyrightTestimonials } from "../data/mock";
import SEO from "../components/SEO";

const CopyrightRegistration = () => {
  const processSteps = [
    {
      step: 1,
      title: "Submit Your Work",
      description: "Provide details about your original creative work.",
      icon: FileText,
    },
    {
      step: 2,
      title: "We File With USPTO",
      description: "Our team prepares and submits your copyright application.",
      icon: Shield,
    },
    {
      step: 3,
      title: "Receive Certificate",
      description: "Get your official copyright registration certificate.",
      icon: Award,
    },
  ];

  const whyYouNeedIt = [
    {
      icon: Lock,
      title: "Legal Ownership",
      description:
        "Establish yourself as the legal owner of your creative work.",
    },
    {
      icon: Shield,
      title: "Protection from Theft",
      description:
        "Prevent others from copying or stealing your original content.",
    },
    {
      icon: DollarSign,
      title: "Monetization Rights",
      description:
        "License, sell, or distribute your work with full legal backing.",
    },
    {
      icon: Book,
      title: "Public Record",
      description:
        "Create an official public record of your copyright ownership.",
    },
  ];

  const whyChooseUs = [
    {
      icon: Zap,
      title: "Fast Filing",
      description:
        "Quick processing and submission to the U.S. Copyright Office",
    },
    {
      icon: Users,
      title: "Expert Review",
      description: "Thorough review by copyright specialists",
    },
    {
      icon: CheckCircle,
      title: "Accuracy Guaranteed",
      description: "We ensure your application is complete and correct",
    },
    {
      icon: Clock,
      title: "Full Support",
      description: "Dedicated assistance throughout the process",
    },
  ];

  const benefits = [
    {
      title: "Legal Evidence",
      description:
        "Copyright registration serves as legal proof of ownership in court.",
    },
    {
      title: "Statutory Damages",
      description:
        "Eligible for statutory damages and attorney fees in infringement cases.",
    },
    {
      title: "Public Notice",
      description: "Creates a public record that deters potential infringers.",
    },
    {
      title: "International Protection",
      description:
        "Establishes your rights for international copyright treaties.",
    },
    {
      title: "Business Asset",
      description:
        "Copyrights are valuable assets that can be sold or licensed.",
    },
    {
      title: "Priority Protection",
      description:
        "Registration date establishes your priority over later claims.",
    },
  ];

  const faqs = [
    {
      id: 1,
      question: "What types of work can be copyrighted?",
      answer:
        "You can copyright literary works, music, art, software, photography, videos, and other original creative works.",
    },
    {
      id: 2,
      question: "How long does copyright protection last?",
      answer:
        "Copyright protection typically lasts for the life of the author plus 70 years.",
    },
    {
      id: 3,
      question: "Do I need to register my copyright?",
      answer:
        "While copyright exists automatically, registration provides legal advantages and is required to file infringement lawsuits.",
    },
    {
      id: 4,
      question: "How long does the registration process take?",
      answer:
        "The U.S. Copyright Office typically takes 3-8 months to process applications.",
    },
    {
      id: 5,
      question: "What's the difference between copyright and trademark?",
      answer:
        "Copyright protects creative works, while trademarks protect brand names, logos, and slogans used in commerce.",
    },
    {
      id: 6,
      question: "Can I copyright an idea?",
      answer:
        "No, copyright protects the expression of ideas, not the ideas themselves. Ideas must be fixed in a tangible form.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Copyright Registration Services | Trademark Claim Experts"
        description="Protect your original content with reliable copyright registration services. We assist with filing copyrights for books, music, artwork, websites, and digital content."
      />
      {/* Banner */}
      <section className="py-12 bg-gradient-to-r from-brand-purple to-brand-pink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold">
            Copyright Registration
          </h1>
        </div>
      </section>

      {/* 1. About the Service */}
      <section className="py-20 bg-gradient-to-br from-brand-purple/5 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Copyright Registration
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-6">
                Protect your original work and creative assets with official
                copyright coverage. Our expert team helps you register your
                copyright with the U.S. Copyright Office quickly and accurately.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Starting at just $99, secure your creative rights and protect
                your intellectual property from unauthorized use.
              </p>
              <PopupButton
                size="lg"
                className="bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90 text-white"
              >
                Get Started Now
              </PopupButton>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-xl flex items-center justify-center bg-gradient-to-br from-brand-purple/5 to-white p-4">
                <img
                  src="/assets/img/copyright-registration.svg"
                  alt="Copyright Registration Illustration"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. 3-Step Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Simple, straightforward copyright registration
            </p>
          </div>
          <HowItWorksSlider items={processSteps} />
        </div>
      </section>

      {/* 3. Why You Need It */}
      <section className="py-20 bg-gradient-to-br from-brand-purple/5 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why You Need Copyright Registration
            </h2>
            <p className="text-lg text-gray-600">
              Secure your creative work and protect your rights
            </p>
          </div>
          <WhyYouNeedItSlider items={whyYouNeedIt} />
        </div>
      </section>

      {/* 4. Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Copyright Registration Packages
            </h2>
            <p className="text-lg text-gray-600">
              Affordable protection for your creative work
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {copyrightPricing.map((plan) => (
              <Card
                key={plan.id}
                className={`relative border-2 flex flex-col h-full ${
                  plan.recommended
                    ? "border-brand-purple shadow-2xl scale-105"
                    : "border-gray-200 hover:border-blue-300"
                } transition-all duration-300 hover:shadow-xl`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-brand-purple to-brand-pink text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-6 pt-8">
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold bg-gradient-to-r from-brand-pink to-brand-purple bg-clip-text text-transparent">
                      ${plan.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-4">
                    {plan.description}
                  </p>
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto max-h-64 pb-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto pt-4">
                  <PopupButton
                    className={`w-full ${
                      plan.recommended
                        ? "bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90"
                        : "bg-gray-800 hover:bg-gray-900"
                    } text-white`}
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </PopupButton>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-brand-purple/5 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us
            </h2>
            <p className="text-lg text-gray-600">
              Professional copyright registration made easy
            </p>
          </div>
          <WhyChooseUsSlider items={whyChooseUs} />
        </div>
      </section>

      {/* 6. Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Key Benefits
            </h2>
            <p className="text-lg text-gray-600">
              What copyright registration offers you
            </p>
          </div>
          <BenefitsSlider benefits={benefits} />
        </div>
      </section>

      {/* 7. Testimonials */}
      <section className="py-20 bg-gradient-to-br from-brand-purple/5 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600">
              Trusted by creators and businesses
            </p>
          </div>
          <TestimonialSlider testimonials={copyrightTestimonials} />
        </div>
      </section>

      {/* 8. FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about copyright
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={`item-${faq.id}`}
                className="border-b border-gray-200"
              >
                <AccordionTrigger className="text-left hover:text-brand-purple transition-colors">
                  <span className="font-semibold text-gray-900">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 9. CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-purple to-brand-pink text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Protect Your Creative Work?
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            Register your copyright today and secure your rights as a creator.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <PopupButton
              size="lg"
              className="bg-white text-brand-purple hover:bg-gray-100"
            >
              Start Your Registration
              <ArrowRight className="ml-2 w-5 h-5" />
            </PopupButton>
            <Link to="/contact-us">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-brand-purple"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CopyrightRegistration;
